import type { GTMCartItemType } from "@/features/analytics/types/event.type";
import type {
  CartItem,
  CartPricing,
  CartProductItem,
} from "@/features/cart/types";

// GTM `value` leaves the addon deposits out (MPL-3933): a deposit is a
// refundable fee, not revenue. The quotes keep it inside `final` (of every
// addon and product entry, and so of the cart and order totals summed from
// them) and report that share in `deposit`. Item `price` already excludes it
// (MPL-3873).

type ItemWithAddons = Pick<CartItem, "subItemsPricing">;

const getAddonsDeposit = (items: ItemWithAddons[]): number =>
  items.reduce(
    (sum, item) => sum + (item.subItemsPricing?.deposit.gross ?? 0),
    0,
  );

const getProductsDeposit = (
  productItems: Pick<CartProductItem, "pricing">[],
): number =>
  productItems.reduce(
    (sum, productItem) => sum + productItem.pricing.deposit.gross,
    0,
  );

const GROSZE_PER_ZLOTY = 100;

// Whole grosze, so the subtraction never reports 120.99999999999999.
const toGtmAmount = (amount: number): number =>
  Math.round(amount * GROSZE_PER_ZLOTY) / GROSZE_PER_ZLOTY;

// `value` from `pricing` of a cart or an order: the diets, their addons and the
// delivery. The Foodango products and their deposits are not in it.
export const getPricingGtmValue = ({
  pricing,
  items,
}: {
  pricing?: Pick<CartPricing, "final"> | null;
  items: ItemWithAddons[];
}): number =>
  pricing ? toGtmAmount(pricing.final.gross - getAddonsDeposit(items)) : 0;

// `value` from `totalPricing` (read through getCartTotalPricing): `pricing`
// plus the Foodango products, whose deposits are inside it too.
export const getTotalPricingGtmValue = ({
  totalPricing,
  items,
  productItems,
}: {
  totalPricing?: Pick<CartPricing, "final"> | null;
  items: ItemWithAddons[];
  productItems?: Pick<CartProductItem, "pricing">[];
}): number =>
  totalPricing
    ? toGtmAmount(
        totalPricing.final.gross -
          getAddonsDeposit(items) -
          getProductsDeposit(productItems ?? []),
      )
    : 0;

// `value` of one cart position: the diet and its addons. The diet `pricing` of
// a position leaves the addons out, they are quoted in `subItemsPricing`.
export const getCartItemGtmValue = ({
  pricing,
  subItemsPricing,
}: Pick<CartItem, "pricing" | "subItemsPricing">): number =>
  toGtmAmount(
    pricing.final.gross +
      (subItemsPricing?.final.gross ?? 0) -
      (subItemsPricing?.deposit.gross ?? 0),
  );

// `value` of a diet with the addon items sent beside it: their `price` already
// leaves the deposit out, so the value equals what the items report.
export const getDietWithAddonsGtmValue = ({
  dietTotal,
  addonItems,
}: {
  dietTotal: number;
  addonItems: Pick<GTMCartItemType, "price" | "quantity">[];
}): number =>
  toGtmAmount(
    addonItems.reduce(
      (sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 0),
      dietTotal,
    ),
  );
